/**
 * Firestore-backed storage API for playground mode.
 *
 * Implements the same storage interface as the mobile app, using direct Firestore writes.
 *
 * Layout (v2, collection-per-key):
 *   - Bucket metadata doc: profiles/{profileId}/h5AppStorage/{appId}  → { __version: 2, lastUpdated }
 *   - One doc per key:      profiles/{profileId}/h5AppStorage/{appId}/items/{encodedKey} → { value, __key }
 *
 * The original (v1) layout stored every key as a flat field on the bucket doc.
 * On first access this client lazily migrates any v1 data into the items
 * subcollection (see ensureV2). Doc IDs are encodeURIComponent-encoded because
 * '/' is a valid storage key but illegal in a Firestore doc ID; the original
 * key is preserved in the __key field.
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
 * Do NOT import from barrel exports (storage/index.ts) - use direct import path instead.
 * This ensures Firebase is not bundled in production builds.
 */
import { CompareAndSwapResult, StorageApi } from './StorageApi'
import { getFirebaseClient, type FirebaseClient } from '../firebase/firebaseClient'

const METADATA_PREFIX = '__'
const VERSION_FIELD = '__version'
const STORAGE_VERSION = 2
const KEY_FIELD = '__key'
const LAST_UPDATED_FIELD = 'lastUpdated'
// Firestore caps a single writeBatch at 500 operations. Chunk below that so a
// large bucket (or a v1 flat doc with many keys being migrated) never overflows
// a batch.
const FIRESTORE_BATCH_CHUNK = 400

function encodeKeyAsDocId(key: string): string {
  return encodeURIComponent(key)
}

function decodeDocIdToKey(docId: string): string {
  return decodeURIComponent(docId)
}

/**
 * Firestore-backed implementation of StorageApi.
 */
export class FirestoreStorageApi implements StorageApi {
  private readonly appId: string
  private client: FirebaseClient | null = null
  // True once the bucket is confirmed to be v2 (already v2, freshly migrated,
  // or empty). A bucket never reverts to v1, so this is safe to cache for the
  // lifetime of the instance.
  private v2Confirmed = false

  constructor(appId: string) {
    this.appId = appId
  }

  private async getClient(): Promise<FirebaseClient> {
    if (!this.client) {
      this.client = await getFirebaseClient()
    }
    return this.client
  }

  private getProfileId(): string {
    if (!this.client) {
      throw new Error('[RUN] Firebase not initialized. Call getClient() first.')
    }
    const profileId = this.client.getProfileId()
    if (!profileId) {
      throw new Error('[RUN] Not signed in. Click the RUN.world toolbar to sign in with Google.')
    }
    return profileId
  }

  private async getMetadataDocRef() {
    const client = await this.getClient()
    const profileId = this.getProfileId()
    return client.doc(client.firestore, `profiles/${profileId}/h5AppStorage/${this.appId}`)
  }

  private async getItemsCollectionRef() {
    const client = await this.getClient()
    const profileId = this.getProfileId()
    return client.collection(client.firestore, `profiles/${profileId}/h5AppStorage/${this.appId}/items`)
  }

  private async getItemDocRef(key: string) {
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()
    return client.doc(itemsCol, encodeKeyAsDocId(key))
  }

  /**
   * Lazily migrate a v1 (flat-document) bucket to the v2 subcollection layout.
   * No-op when the bucket is already v2 or doesn't exist yet. Cached so this
   * runs at most one read per instance once confirmed.
   */
  private async ensureV2(): Promise<void> {
    if (this.v2Confirmed) return

    const client = await this.getClient()
    const metaRef = await this.getMetadataDocRef()
    const metaSnap = await client.getDoc(metaRef)

    if (!metaSnap.exists()) {
      this.v2Confirmed = true
      return
    }

    const data = metaSnap.data() || {}
    if (data[VERSION_FIELD] === STORAGE_VERSION) {
      this.v2Confirmed = true
      return
    }

    // v1 flat doc → migrate every user key into the items subcollection, then
    // replace the metadata doc with the version marker (dropping flat fields).
    // A v1 flat doc can hold more keys than fit in one batch, so chunk the item
    // writes; replace the metadata doc last so an interrupted migration re-runs
    // safely (it stays v1 until the final write lands).
    const itemsCol = await this.getItemsCollectionRef()
    // Filter all metadata: any __-prefixed field (incl. __version) plus
    // lastUpdated — matches the server's extractUserData so playground and server
    // agree on what is user data.
    const entries = Object.entries(data).filter(
      ([key]) => !key.startsWith(METADATA_PREFIX) && key !== LAST_UPDATED_FIELD,
    )
    await this.commitChunked(client, entries, (batch, [key, value]) => {
      batch.set(client.doc(itemsCol, encodeKeyAsDocId(key)), {
        value: typeof value === 'string' ? value : JSON.stringify(value),
        [KEY_FIELD]: key,
      })
    })
    await client.setDoc(metaRef, {
      [VERSION_FIELD]: STORAGE_VERSION,
      [LAST_UPDATED_FIELD]: client.serverTimestamp(),
    })

    this.v2Confirmed = true
  }

  /**
   * Commit writes in batches under Firestore's 500-operation limit.
   */
  private async commitChunked<T>(
    client: FirebaseClient,
    entries: T[],
    apply: (batch: ReturnType<FirebaseClient['writeBatch']>, entry: T) => void,
  ): Promise<void> {
    for (let i = 0; i < entries.length; i += FIRESTORE_BATCH_CHUNK) {
      const chunk = entries.slice(i, i + FIRESTORE_BATCH_CHUNK)
      const batch = client.writeBatch(client.firestore)
      for (const entry of chunk) apply(batch, entry)
      await batch.commit()
    }
  }

  /** Stamp the bucket metadata doc with the version marker after a write. */
  private async touchMetadata(): Promise<void> {
    const client = await this.getClient()
    const metaRef = await this.getMetadataDocRef()
    await client.setDoc(
      metaRef,
      {
        [VERSION_FIELD]: STORAGE_VERSION,
        [LAST_UPDATED_FIELD]: client.serverTimestamp(),
      },
      { merge: true },
    )
  }

  /**
   * Refresh the metadata doc's lastUpdated after a removal. Uses updateDoc so it
   * no-ops when the bucket has no metadata doc (never creates an orphan) — unlike
   * touchMetadata, which is for writes that should create/stamp the doc.
   */
  private async refreshMetadataIfExists(): Promise<void> {
    const client = await this.getClient()
    const metaRef = await this.getMetadataDocRef()
    try {
      await client.updateDoc(metaRef, { [LAST_UPDATED_FIELD]: client.serverTimestamp() })
    } catch (error) {
      const err = error as { code?: string }
      if (err.code !== 'not-found') {
        throw error
      }
    }
  }

  private async readAllItems(): Promise<Record<string, string>> {
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()
    const snapshot = await client.getDocs(itemsCol)

    const result: Record<string, string> = {}
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data() as { value?: unknown; __key?: string }
      const key = data[KEY_FIELD] ?? decodeDocIdToKey(docSnap.id)
      const value = data.value
      result[key] = typeof value === 'string' ? value : JSON.stringify(value)
    }
    return result
  }

  async getItem(key: string): Promise<string | null> {
    await this.ensureV2()
    const client = await this.getClient()
    const itemRef = await this.getItemDocRef(key)
    const snapshot = await client.getDoc(itemRef)

    if (!snapshot.exists()) {
      return null
    }

    const value = (snapshot.data() as { value?: unknown }).value
    if (value === undefined || value === null) {
      return null
    }

    return typeof value === 'string' ? value : JSON.stringify(value)
  }

  async setItem(key: string, value: string): Promise<void> {
    await this.ensureV2()
    const client = await this.getClient()
    const itemRef = await this.getItemDocRef(key)

    await client.setDoc(itemRef, { value, [KEY_FIELD]: key })
    await this.touchMetadata()
  }

  async removeItem(key: string): Promise<void> {
    await this.ensureV2()
    const client = await this.getClient()
    const itemRef = await this.getItemDocRef(key)

    try {
      await client.deleteDoc(itemRef)
    } catch (error) {
      // Document might not exist, which is fine for removeItem
      const err = error as { code?: string }
      if (err.code !== 'not-found') {
        throw error
      }
    }
    await this.refreshMetadataIfExists()
  }

  async clear(): Promise<void> {
    await this.ensureV2()
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()
    const snapshot = await client.getDocs(itemsCol)

    await this.commitChunked(client, snapshot.docs, (batch, docSnap) => {
      batch.delete(docSnap.ref)
    })

    const metaRef = await this.getMetadataDocRef()
    try {
      await client.deleteDoc(metaRef)
    } catch (error) {
      // Document might not exist, which is fine for clear
      const err = error as { code?: string }
      if (err.code !== 'not-found') {
        throw error
      }
    }
  }

  async key(index: number): Promise<string | null> {
    await this.ensureV2()
    const keys = Object.keys(await this.readAllItems())

    if (index < 0 || index >= keys.length) {
      return null
    }

    return keys[index]
  }

  async length(): Promise<number> {
    await this.ensureV2()
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()
    const snapshot = await client.getDocs(itemsCol)
    return snapshot.size
  }

  async getAllItems(): Promise<string[]> {
    await this.ensureV2()
    return Object.keys(await this.readAllItems())
  }

  async getAllData(): Promise<Record<string, string>> {
    await this.ensureV2()
    return this.readAllItems()
  }

  async setMultipleItems(items: { key: string; value: string }[]): Promise<void> {
    if (items.length === 0) {
      return
    }

    await this.ensureV2()
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()

    await this.commitChunked(client, items, (batch, item) => {
      batch.set(client.doc(itemsCol, encodeKeyAsDocId(item.key)), {
        value: item.value,
        [KEY_FIELD]: item.key,
      })
    })
    await this.touchMetadata()
  }

  async removeMultipleItems(keys: string[]): Promise<void> {
    // No-op on empty input to match MockStorageApi behavior.
    if (keys.length === 0) {
      return
    }

    await this.ensureV2()
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()

    await this.commitChunked(client, keys, (batch, key) => {
      batch.delete(client.doc(itemsCol, encodeKeyAsDocId(key)))
    })
    await this.refreshMetadataIfExists()
  }

  /**
   * Playground CAS: a client-side Firestore transaction against the legacy
   * bucket path. Atomicity is best-effort here — the playground bypasses
   * Cloud Run and is guarded only by ownership rules — but the observable
   * contract (result shape, no write on mismatch) matches hosted storage.
   */
  async compareAndSwap(
    key: string,
    expectedValue: string | null,
    nextValue: string | null,
  ): Promise<CompareAndSwapResult> {
    await this.ensureV2()
    const client = await this.getClient()
    const itemRef = await this.getItemDocRef(key)

    const result = await client.runTransaction(client.firestore, async (txn) => {
      const snapshot = await txn.get(itemRef)
      const raw = snapshot.exists() ? (snapshot.data() as { value?: unknown }).value : null
      const currentValue =
        raw === undefined || raw === null ? null : typeof raw === 'string' ? raw : JSON.stringify(raw)
      if (currentValue !== expectedValue) {
        return { swapped: false as const, currentValue }
      }
      if (nextValue === null) {
        txn.delete(itemRef)
      } else {
        txn.set(itemRef, { value: nextValue, [KEY_FIELD]: key })
      }
      return { swapped: true as const }
    })

    // Metadata stamping mirrors setItem/removeItem: a successful set stamps
    // (creating the doc if needed); a successful delete only refreshes an
    // existing doc so deleting the last key never leaves an orphan.
    if (result.swapped) {
      if (nextValue === null) {
        await this.refreshMetadataIfExists()
      } else {
        await this.touchMetadata()
      }
    }
    return result
  }

  async getMultipleItems(keys: string[]): Promise<Record<string, string>> {
    const uniqueKeys = Array.from(new Set(keys))
    if (uniqueKeys.length === 0) {
      return {}
    }
    await this.ensureV2()
    const client = await this.getClient()
    const itemsCol = await this.getItemsCollectionRef()

    const snapshots = await Promise.all(
      uniqueKeys.map((key) => client.getDoc(client.doc(itemsCol, encodeKeyAsDocId(key)))),
    )
    const result: Record<string, string> = {}
    snapshots.forEach((snapshot, i) => {
      if (!snapshot.exists()) return
      const raw = (snapshot.data() as { value?: unknown }).value
      if (raw === undefined || raw === null) return
      result[uniqueKeys[i]] = typeof raw === 'string' ? raw : JSON.stringify(raw)
    })
    return result
  }
}
